import React, { useEffect, useState } from "react";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { SectionCard, StatCard, StatGrid } from "../../components/admin/AdminCard";
import MiniAreaChart from "../../components/admin/MiniAreaChart";
import {
  getMyAffiliate,
  getClubAffiliateSummary,
  requestPayout,
  type MyAffiliate,
  type ClubAffiliateSummary,
} from "../../services/affiliate";
import { formatDate } from "../../lib/dateFormat";

function euros(cents: number) {
  return `${(cents / 100).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function CopyLink({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 px-3 py-2 text-sm break-all rounded-lg bg-slate-100 text-slate-700">{link}</code>
      <button
        onClick={() => { navigator.clipboard?.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}
        className="px-3 py-2 text-sm font-semibold text-white rounded-lg bg-indigo-600 shrink-0"
      >
        {copied ? "Copiat ✓" : "Copiază"}
      </button>
    </div>
  );
}

export default function AffiliateDashboard() {
  const [me, setMe] = useState<MyAffiliate | null>(null);
  const [summary, setSummary] = useState<ClubAffiliateSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const m = await getMyAffiliate();
      setMe(m);
      if (m.club) setSummary(await getClubAffiliateSummary(m.club.id));
    } catch (e: any) {
      setErr(e?.response?.data?.error || "Nu am putut încărca datele de afiliere.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submitPayout = async () => {
    if (!me?.club) return;
    setErr(null); setMsg(null);
    try {
      await requestPayout(me.club.id, Math.round((parseFloat(amount) || 0) * 100));
      setMsg("Cerere de retragere trimisă.");
      setAmount("");
      setSummary(await getClubAffiliateSummary(me.club.id));
    } catch (e: any) {
      setErr(e?.response?.data?.error || "Cererea a eșuat.");
    }
  };

  return (
    <ClubAdminLayout>
    <div className="space-y-6">
      <AdminPageHeader title="Programul de afiliere"
        icon={<ClubNavIcon id="affiliate" className="w-6 h-6" />}
        subtitle="Invită cluburi, antrenori și familii. Câștigi când cei aduși își activează un cont plătit." />

      {err && <div className="mb-4 p-3 text-sm rounded-xl bg-rose-50 border border-rose-200 text-rose-700">{err}</div>}
      {msg && <div className="mb-4 p-3 text-sm rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700">{msg}</div>}

      {loading ? (
        <div className="py-10 text-center text-slate-400">Se încarcă…</div>
      ) : (
        <div className="space-y-6">
          {me?.personal && (
            <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
              <h2 className="mb-1 font-bold text-slate-800">Link-ul tău de afiliat</h2>
              <p className="mb-3 text-sm text-slate-500">Cod: <span className="font-mono">{me.personal.code}</span></p>
              <CopyLink link={me.personal.link} />
            </div>
          )}

          {me && (
            <StatGrid cols={2}>
              <StatCard label="Recomandările tale" value={me.referrals.total} dotClass="bg-indigo-500" />
              <StatCard label="Convertite (plătite)" value={me.referrals.converted} dotClass="bg-emerald-500" valueClass="text-emerald-600" />
            </StatGrid>
          )}

          {me?.club && summary && (
            <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
              <h2 className="mb-1 font-bold text-slate-800">Clubul tău</h2>
              <p className="mb-3 text-sm text-slate-500">Link club: <span className="font-mono">{me.club.code}</span></p>
              <CopyLink link={me.club.link} />

              <div className="flex items-center justify-between p-3 mt-4 rounded-xl bg-indigo-50">
                <span className="text-sm font-semibold text-indigo-900">Sold din comisioane</span>
                <span className="text-xl font-extrabold text-indigo-700">{euros(summary.balance_cents)}</span>
              </div>

              <div className="flex items-end gap-2 mt-3">
                <label className="text-sm">
                  <span className="block text-[11px] text-slate-500">Retrage (€)</span>
                  <input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                    className="w-32 px-2 py-1.5 border rounded-lg border-slate-200" placeholder="0.00" />
                </label>
                <button onClick={submitPayout} disabled={summary.balance_cents <= 0}
                  className="px-3 py-2 text-sm font-semibold text-white rounded-lg bg-indigo-600 disabled:opacity-60">
                  Cere retragere
                </button>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">Soldul scade automat factura lunară a clubului; surplusul poate fi retras (procesat manual).</p>

              {summary.payouts.length > 0 && (
                <div className="mt-4">
                  <div className="mb-1 text-xs font-semibold text-slate-500">Cereri de retragere</div>
                  {summary.payouts.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-1.5 text-sm border-b border-slate-100">
                      <span>{euros(p.amount_cents)}</span>
                      <span className="text-xs text-slate-500">{p.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {me?.club && summary && (
            <>
              {/* Stats */}
              <StatGrid cols={4}>
                <StatCard label="Invitații trimise" value={summary.stats.invitations_sent} dotClass="bg-sky-500" />
                <StatCard label="Invitații acceptate" value={summary.stats.invitations_accepted} dotClass="bg-violet-500" />
                <StatCard label="Recomandări" value={summary.stats.referrals_total} dotClass="bg-indigo-500" />
                <StatCard label="Convertite (plătit)" value={summary.stats.referrals_converted} dotClass="bg-emerald-500" valueClass="text-emerald-600" />
              </StatGrid>

              {/* Commission revenue trend — monthly totals from the money
                  ledger. Shown only when there are at least two periods to
                  plot; otherwise the list below tells the whole story. */}
              {(() => {
                const byPeriod = new Map<string, number>();
                summary.ledger
                  .filter((l) => l.kind === "money" && l.period)
                  .forEach((l) => {
                    const key = l.period as string;
                    byPeriod.set(key, (byPeriod.get(key) ?? 0) + (l.amount_cents ?? 0));
                  });
                const periods = Array.from(byPeriod.keys()).sort();
                if (periods.length < 2) return null;
                const series = periods.map((p) => Math.max(0, byPeriod.get(p) ?? 0) / 100);
                const labels = periods.map((p) => {
                  const [y, m] = p.split("-");
                  return m ? `${m}/${(y || "").slice(2)}` : p;
                });
                return (
                  <SectionCard title="Venituri din comisioane" subtitle="pe luni" bodyPadded>
                    <MiniAreaChart
                      data={series}
                      labels={labels}
                      color="#10b981"
                      height={170}
                      formatValue={(v) => `${v.toLocaleString("ro-RO", { maximumFractionDigits: 0 })} €`}
                      className="text-slate-400"
                    />
                  </SectionCard>
                );
              })()}

              {/* Referrals history */}
              <SectionCard title="Sportivi aduși" bodyPadded>
                {summary.referrals.length === 0 ? (
                  <p className="text-sm text-slate-400">Încă niciun sportiv adus prin afiliere.</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {summary.referrals.map((r) => {
                      // The plan they hold NOW. `plan_code` is what the
                      // commission was earned on and can be months stale.
                      const plan = r.current_plan_code || "free";
                      const paying = plan !== "free";
                      return (
                        <div key={r.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2.5 text-sm">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate text-slate-700">{r.name}</p>
                            <p className="text-xs truncate text-slate-400">
                              {r.team?.name || "Fără echipă"}
                              {r.coach?.name ? ` · ${r.coach.name}` : ""}
                            </p>
                          </div>
                          <span
                            className={`px-2 py-0.5 text-[11px] font-semibold rounded-full capitalize ${
                              paying ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                            }`}
                            title="Planul curent al sportivului"
                          >
                            {plan}
                          </span>
                          {r.status !== "converted" && (
                            <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-50 text-amber-700">
                              comision în așteptare
                            </span>
                          )}
                          <span className="text-xs text-slate-400">{formatDate(new Date(r.created_at))}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </SectionCard>

              {/* Revenue history */}
              <SectionCard title="Istoric venituri" bodyPadded>
                {summary.ledger.filter((l) => l.kind === "money").length === 0 ? (
                  <p className="text-sm text-slate-400">Încă niciun venit din comisioane.</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {summary.ledger.filter((l) => l.kind === "money" || l.kind === "payout" || l.kind === "invoice_offset").map((l) => (
                      <div key={l.id} className="flex items-center gap-2 py-2 text-sm">
                        <span className="flex-1 text-slate-600">
                          {l.kind === "money" ? `Comision${l.period ? ` · ${l.period}` : ""}` : l.kind === "payout" ? "Retragere" : "Aplicat pe factură"}
                        </span>
                        <span className={`font-semibold ${(l.amount_cents ?? 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                          {(l.amount_cents ?? 0) >= 0 ? "+" : ""}{euros(l.amount_cents ?? 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </>
          )}
        </div>
      )}
    </div>
    </ClubAdminLayout>
  );
}
